import { z } from 'zod'

import { config } from '../config'
import { log } from '../log'
import { session } from './session'
import type { TokenPair } from './session'

/**
 * RGS client.
 *
 * Server responses are validated by a Zod schema rather than cast with
 * `as SpinResponse`. A cast is a promise to the compiler that the runtime
 * never checks: let the backend change one field and the client breaks
 * somewhere deep inside the animation with `undefined is not a function`.
 * The schema catches the mismatch at the boundary, where it is still
 * visible.
 */

export const GameModeSchema = z.enum(['base', 'ante', 'buy'])
export type GameMode = z.infer<typeof GameModeSchema>

export const MechanicSchema = z.enum(['ways', 'tumble', 'lines'])
export type Mechanic = z.infer<typeof MechanicSchema>

// ── Ways / lines ──

/** lines: one paid line, positions are the first `count` cells (docs/lines-round-contract.md §3). */
const LineWinSchema = z.object({
  line: z.number().int(),
  symbol: z.string(),
  count: z.number().int(),
  winCoins: z.number(),
  positions: z.array(z.object({ reel: z.number().int(), row: z.number().int() })),
})

const SpinViewSchema = z.object({
  kind: z.enum(['base', 'free']),
  window: z.array(z.array(z.string())),
  multiplier: z.number(),
  winCoins: z.number(),
  /** lines only. */
  lineWins: z.array(LineWinSchema).optional(),
  scatterCount: z.number().optional(),
  scatterWinCoins: z.number().optional(),
})

/** Risk Game offer, in the player's coins (docs/lines-round-contract.md §3). */
const GambleOfferSchema = z.object({
  available: z.boolean(),
  reason: z.enum(['disabled', 'no-win', 'max-steps', 'over-limit']).optional(),
  stakeCoins: z.number(),
  potentialWinCoins: z.number(),
  step: z.number(),
  maxSteps: z.number(),
})

/** One gamble step (docs/lines-round-contract.md §4). */
const GambleResponseSchema = z.object({
  roundId: z.string(),
  step: z.number(),
  action: z.enum(['double', 'collect']),
  card: z.number().int().nullable(),
  colour: z.enum(['red', 'black']).nullable(),
  won: z.boolean().nullable(),
  stakeCoins: z.number(),
  payoutCoins: z.number(),
  closed: z.boolean(),
  gamble: GambleOfferSchema,
  balanceCoins: z.number(),
})

// ── Tumble: the RoundResult contract (docs/bird-rush-round-contract.md) ──
// Money here is in the math's REFERENCE coins; multiply by betMultiplier.

const TumbleFrameSchema = z.object({
  /** grid[reel * rows + row]; ≥0 — symbol id, <0 — bomb with multiplier −v. */
  grid: z.array(z.number().int()),
  wins: z.array(z.object({ symbol: z.number(), count: z.number(), pay: z.number() })),
  win: z.number(),
})

const TumbleSpinSchema = z.object({
  kind: z.enum(['base', 'free']),
  frames: z.array(TumbleFrameSchema).min(1),
  scatterCount: z.number(),
  scatterPayCoins: z.number(),
  tumbleWin: z.number(),
  bombSum: z.number(),
  totalWin: z.number(),
  /** Persistent games (Gates class) only: the multiplier that applied / the accumulator after the spin. */
  multApplied: z.number().optional(),
  globalMultAfter: z.number().optional(),
})

const TumbleResultSchema = z.object({
  gameId: z.string(),
  mathVersion: z.string(),
  mode: GameModeSchema,
  betChargedCoins: z.number(),
  roundWin: z.number(),
  baseWin: z.number(),
  fsWin: z.number(),
  fsTriggered: z.boolean(),
  fsSpinsPlayed: z.number(),
  fsRetriggers: z.number(),
  capped: z.boolean(),
  trace: z.array(TumbleSpinSchema).min(1),
})

// ── Responses ──

const SpinResponseSchema = z
  .object({
    roundId: z.string(),
    gameId: z.string(),
    mathVersion: z.string(),
    mechanic: MechanicSchema,
    mode: GameModeSchema,
    betCoins: z.number(),
    chargedCoins: z.number(),
    betMultiplier: z.number(),
    winCoins: z.number(),
    baseWinCoins: z.number(),
    freeSpinWinCoins: z.number(),
    capped: z.boolean(),
    freeSpins: z.object({
      triggered: z.boolean(),
      played: z.number(),
      retriggers: z.number(),
    }),
    spins: z.array(SpinViewSchema).min(1).optional(),
    result: TumbleResultSchema.optional(),
    /** lines only: the Risk Game offer for this round. */
    gamble: GambleOfferSchema.optional(),
    balanceCoins: z.number(),
  })
  .superRefine((r, ctx) => {
    if ((r.mechanic === 'ways' || r.mechanic === 'lines') && !r.spins) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: `${r.mechanic} response without spins`, path: ['spins'] })
    }
    if (r.mechanic === 'tumble' && !r.result) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'tumble response without result', path: ['result'] })
    }
  })

const BalanceResponseSchema = z.object({
  playerId: z.string(),
  balanceCoins: z.number(),
  currency: z.string(),
  allowedBetsCoins: z.array(z.number()).min(1),
  defaultBetCoins: z.number(),
  modes: z.array(GameModeSchema).min(1),
  mechanic: MechanicSchema,
  gameId: z.string(),
  mathVersion: z.string(),
})

/**
 * Math provenance: the sha256 of the file that is playing right now. The
 * version in the config can say anything — the hash is checked against the
 * release manifest on the server and is the only part that cannot be faked.
 */
const MathProvenanceSchema = z.object({
  sha256: z.string(),
  kind: z.enum(['release', 'config']),
  file: z.string(),
  releasedAt: z.string().nullable(),
})

const GameInfoSchema = z.object({
  gameId: z.string(),
  name: z.string(),
  mathVersion: z.string(),
  math: MathProvenanceSchema,
  mechanic: MechanicSchema,
  layout: z.object({ reels: z.number(), rows: z.number() }),
  refBetCoins: z.number(),
  maxWinMultiplier: z.number(),
  modes: z.array(GameModeSchema),
  symbols: z.array(z.object({ id: z.number(), code: z.string(), name: z.string(), kind: z.string() })),
  paytable: z.record(z.array(z.number())),
  payKeys: z.array(z.number()),
  scatterPays: z.record(z.number()),
  freeSpins: z.object({
    awards: z.record(z.number()),
    retrigger: z.object({ minScatters: z.number(), spins: z.number() }).nullable(),
  }),
  bombValues: z.array(z.number()).optional(),
  bombRules: z.object({ inBaseGame: z.boolean(), persistent: z.boolean() }).optional(),
  anteBet: z.object({ costMultiplier: z.number() }).nullable().optional(),
  buyFeature: z.object({ costMultiplier: z.number() }).nullable().optional(),
  /** lines only: paylines[i][reel] = row (docs/lines-round-contract.md §5). */
  paylines: z.array(z.array(z.number().int())).optional(),
  /** lines only: Risk Game limits, or null when the game has no gamble. */
  gamble: z.object({ maxSteps: z.number(), maxStakeMultiplier: z.number() }).nullable().optional(),
})

/**
 * A game card in the lobby. There is deliberately no paytable and no
 * free-spin rules here: the list must load in one light request, and the
 * details arrive when entering the game (GET /games/:id).
 */
const GameSummarySchema = z.object({
  gameId: z.string(),
  name: z.string(),
  mathVersion: z.string(),
  mechanic: MechanicSchema,
  layout: z.object({ reels: z.number(), rows: z.number() }),
  refBetCoins: z.number(),
  maxWinMultiplier: z.number(),
  modes: z.array(GameModeSchema),
  allowedBetsCoins: z.array(z.number()).min(1),
  defaultBetCoins: z.number(),
  symbols: z.array(z.object({ id: z.number(), code: z.string(), name: z.string(), kind: z.string() })),
})

const GamesResponseSchema = z.object({
  defaultGameId: z.string(),
  games: z.array(GameSummarySchema).min(1),
})

export type GameSummary = z.infer<typeof GameSummarySchema>
export type GamesResponse = z.infer<typeof GamesResponseSchema>

export type SpinView = z.infer<typeof SpinViewSchema>
export type LineWin = z.infer<typeof LineWinSchema>
export type GambleOffer = z.infer<typeof GambleOfferSchema>
export type GambleResponse = z.infer<typeof GambleResponseSchema>
export type GambleAction = GambleResponse['action']
export type GambleGuess = 'red' | 'black'
export type TumbleFrame = z.infer<typeof TumbleFrameSchema>
export type TumbleSpin = z.infer<typeof TumbleSpinSchema>
export type TumbleResult = z.infer<typeof TumbleResultSchema>
export type SpinResponse = z.infer<typeof SpinResponseSchema>
export type BalanceResponse = z.infer<typeof BalanceResponseSchema>
export type GameInfo = z.infer<typeof GameInfoSchema>
export type MathProvenance = z.infer<typeof MathProvenanceSchema>

export class ApiError extends Error {
  readonly status: number
  readonly payload: unknown

  constructor(status: number, message: string, payload: unknown) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.payload = payload
  }
}

const TokenPairSchema = z.object({
  accessToken: z.string(),
  tokenType: z.literal('Bearer'),
  expiresIn: z.number(),
  refreshToken: z.string(),
  playerId: z.string(),
  gameId: z.string().nullable(),
})

/**
 * One fetch, with authentication attached.
 *
 * `authenticate: false` is for the token endpoints themselves — sending an
 * expired access token to /auth/refresh would be pointless, and to
 * /auth/token actively wrong.
 */
async function send(
  path: string,
  init: RequestInit,
  authenticate: boolean,
): Promise<Response> {
  try {
    return await fetch(`${config.apiBaseUrl}${path}`, {
      ...init,
      headers: {
        'Content-Type': 'application/json',
        ...(authenticate ? session.headers() : {}),
        ...init.headers,
      },
    })
  } catch (cause) {
    // The one failure the player is told nothing useful about: "no
    // connection" covers DNS, TLS, a CORS preflight the operator's origin
    // is missing from, and a server that is simply down. The distinction
    // lives in the console.
    log.error('api.network-failure', cause, 'fetch threw before a response', { path })
    throw new ApiError(0, 'No connection to the game server', cause)
  }
}

/**
 * A single refresh shared by everyone who needs it.
 *
 * Without this, a screen that fires three requests at once on a stale token
 * would run three refreshes; refresh tokens rotate, so two of them would be
 * spent on a token that had already been replaced and the session would end
 * for no reason.
 */
let refreshInFlight: Promise<boolean> | null = null

async function refreshAccessToken(): Promise<boolean> {
  if (refreshInFlight) return refreshInFlight

  refreshInFlight = (async () => {
    const token = session.refreshTokenValue()
    if (!token) return false
    try {
      const pair = await request(
        '/auth/refresh',
        TokenPairSchema,
        { method: 'POST', body: JSON.stringify({ refreshToken: token }) },
        { authenticate: false, retryOnUnauthorized: false },
      )
      session.adopt(pair)
      return true
    } catch (err) {
      // The session is genuinely over: the operator has to launch the game
      // again. Clearing it stops every later request from retrying.
      //
      // Worth a log line even though it is an expected end state: from the
      // player's side an expired session and a broken refresh endpoint look
      // identical, and only one of them is our problem.
      log.warn('auth.refresh-failed', 'the session could not be renewed and was cleared', {
        reason: err instanceof Error ? err.message : String(err),
      })
      session.clear()
      return false
    } finally {
      refreshInFlight = null
    }
  })()

  return refreshInFlight
}

async function request<T>(
  path: string,
  schema: z.ZodSchema<T>,
  init: RequestInit = {},
  options: { authenticate?: boolean; retryOnUnauthorized?: boolean } = {},
): Promise<T> {
  const authenticate = options.authenticate ?? true
  const retryOnUnauthorized = options.retryOnUnauthorized ?? true

  // Renew before the request when the token is nearly out, rather than
  // waiting for a 401: the 401 path costs an extra round trip, and on a
  // spin that shows up as a stutter right after the player presses the
  // button.
  if (authenticate && session.needsRefresh()) await refreshAccessToken()

  let response = await send(path, init, authenticate)

  // The token can also die between the check and the request — a suspended
  // laptop, a slow network, a session revoked server-side.
  if (response.status === 401 && authenticate && retryOnUnauthorized && session.isBearer()) {
    if (await refreshAccessToken()) response = await send(path, init, authenticate)
  }

  const body: unknown = await response.json().catch(() => null)

  if (!response.ok) {
    const message =
      body && typeof body === 'object' && 'message' in body
        ? String((body as { message: unknown }).message)
        : `Error ${response.status}`
    // 4xx is usually the player (out of balance, bet not allowed) and 5xx is
    // always us, so they are not the same news.
    log.error('api.request-failed', undefined, message, {
      path,
      status: response.status,
      method: init.method ?? 'GET',
    })
    throw new ApiError(response.status, message, body)
  }

  const parsed = schema.safeParse(body)
  if (!parsed.success) {
    // The contract between client and RGS just broke. The player sees a
    // generic failure; the issue list is the only thing that says which
    // field moved, so it must not stay inside the thrown object alone.
    log.error('api.shape-mismatch', undefined, `unexpected response shape at ${path}`, {
      path,
      issues: parsed.error.issues,
    })
    throw new ApiError(
      response.status,
      `The server returned an unexpected shape at ${path}`,
      parsed.error.issues,
    )
  }
  return parsed.data
}

/**
 * A fresh idempotency key per money-moving request.
 *
 * It lives in the headers passed to `request`, so an automatic retry after
 * a token refresh reuses the SAME key — which is the point: the server
 * replays the round it already played instead of charging the bet twice.
 */
function moneyHeaders(): Record<string, string> {
  return { 'X-Idempotency-Key': crypto.randomUUID() }
}

const AuthModeSchema = z.object({
  mode: z.string(),
  /** True when this server accepts POST /auth/player-login. */
  playerLogin: z.boolean(),
})

export type AuthMode = z.infer<typeof AuthModeSchema>

export const api = {
  /**
   * Exchanges the one-time launch token from the iframe URL for a token
   * pair. The tokens are adopted here, so every later call is authenticated
   * without the caller passing anything around.
   */
  async exchangeLaunchToken(launchToken: string): Promise<TokenPair> {
    const pair = await request(
      '/auth/token',
      TokenPairSchema,
      { method: 'POST', body: JSON.stringify({ launchToken }) },
      { authenticate: false, retryOnUnauthorized: false },
    )
    session.adopt(pair)
    return pair
  },

  /**
   * Picks a session back up after a page reload, using the refresh token
   * that survived in sessionStorage. Returns null when there is nothing to
   * resume — the caller then falls back to a dev session or reports that
   * the operator has to relaunch the game.
   */
  async resumeSession(): Promise<TokenPair | null> {
    const token = session.storedRefreshToken()
    if (!token) return null
    try {
      const pair = await request(
        '/auth/refresh',
        TokenPairSchema,
        { method: 'POST', body: JSON.stringify({ refreshToken: token }) },
        { authenticate: false, retryOnUnauthorized: false },
      )
      session.adopt(pair)
      return pair
    } catch (err) {
      log.info('auth.resume-failed', 'no session survived the reload', {
        reason: err instanceof Error ? err.message : String(err),
      })
      session.clear()
      return null
    }
  },

  /** Ends this session server-side. Best effort — the tokens are dropped either way. */
  async logout(): Promise<void> {
    try {
      await request('/auth/logout', z.unknown(), { method: 'POST' })
    } catch (err) {
      // A network failure must not leave the player looking logged in — but
      // the server-side session then outlives the client one, which is a
      // fact somebody auditing sessions will want to have seen.
      log.warn('auth.logout-failed', 'tokens dropped locally, server not told', {
        reason: err instanceof Error ? err.message : String(err),
      })
    } finally {
      session.clear()
    }
  },

  /**
   * How this server expects a player to authenticate.
   *
   * Asked rather than assumed: with AUTH_MODE=jwt and no launch token the
   * client must show a login form, with dev-header it opens a dev session.
   * Guessing wrong shows the player a broken game instead of a form.
   */
  authMode(): Promise<AuthMode> {
    return request('/auth/mode', AuthModeSchema, {}, { authenticate: false, retryOnUnauthorized: false })
  },

  /** A login and a password on our own stand. Produces an ordinary session. */
  async playerLogin(login: string, password: string): Promise<TokenPair> {
    const pair = await request(
      '/auth/player-login',
      TokenPairSchema,
      { method: 'POST', body: JSON.stringify({ login, password }) },
      { authenticate: false, retryOnUnauthorized: false },
    )
    session.adopt(pair)
    return pair
  },

  /** Lobby: every game this server is able to serve. */
  games(): Promise<GamesResponse> {
    return request('/games', GamesResponseSchema)
  },

  /**
   * Public description of a game: symbols, pays, modes. Nothing about the
   * outcome. Without a gameId — the default game.
   */
  gameInfo(gameId?: string): Promise<GameInfo> {
    return request(gameId ? `/games/${encodeURIComponent(gameId)}` : '/game', GameInfoSchema)
  },

  /**
   * Creates or picks up a test player. Works outside production only, and
   * only against AUTH_MODE=dev-header.
   */
  async devSession(externalId: string, gameId?: string): Promise<BalanceResponse> {
    const balance = await request(
      '/players/dev-session',
      BalanceResponseSchema,
      { method: 'POST', body: JSON.stringify({ externalId, ...(gameId ? { gameId } : {}) }) },
      { authenticate: false, retryOnUnauthorized: false },
    )
    session.adoptDev(balance.playerId)
    return balance
  },

  balance(gameId?: string): Promise<BalanceResponse> {
    const query = gameId ? `?gameId=${encodeURIComponent(gameId)}` : ''
    return request(`/players/me${query}`, BalanceResponseSchema)
  },

  /**
   * Spin.
   *
   * The idempotency key is generated here and lives for exactly one round:
   * if the response is lost and the request has to be retried, the server
   * returns the same round instead of charging the bet a second time.
   */
  spin(betCoins: number, mode: GameMode, gameId?: string): Promise<SpinResponse> {
    return request('/spin', SpinResponseSchema, {
      method: 'POST',
      headers: moneyHeaders(),
      body: JSON.stringify({ betCoins, mode, ...(gameId ? { gameId } : {}) }),
    })
  },

  /**
   * Risk Game step (docs/lines-round-contract.md §4).
   *
   * Same idempotency mechanism as spin: one fresh key per step, so a
   * retried request replays the same card instead of drawing a second one.
   */
  gamble(roundId: string, action: GambleAction, guess?: GambleGuess): Promise<GambleResponse> {
    return request('/gamble', GambleResponseSchema, {
      method: 'POST',
      headers: moneyHeaders(),
      body: JSON.stringify({ roundId, action, ...(guess ? { guess } : {}) }),
    })
  },
}
